import { isNumber, isString } from 'lodash';
import React from 'react';
import { NativeSyntheticEvent, StyleSheet, TextInputFocusEventData } from 'react-native';
import TextArea from 'react-textarea-autosize';
import { MBTypographyStylesRaw } from '@shared/components/typography';
import { FONT_STACK } from '@shared/styles';
import { UnvalidatedStyles } from '../styles';
import { MBAutosizeTextInputProps as Props } from './props';

interface State {
  focused: boolean;
}

/*
    Text area that adapts its height to the content.
*/
export class MBAutosizeTextInput extends React.PureComponent<Props, State> {
  state: State = { focused: false };
  textarea = React.createRef<HTMLTextAreaElement>();
  focus = () => this.textarea.current?.focus();
  blur = () => this.textarea.current?.blur();

  onFocus = (event: NativeSyntheticEvent<TextInputFocusEventData>) => {
    this.setState(prev => ({ ...prev, focused: true }));
    this.props.onFocus?.(event);
  };

  onBlur = (event: NativeSyntheticEvent<TextInputFocusEventData>) => {
    this.setState(prev => ({ ...prev, focused: false }));
    this.props.onBlur?.(event);
  };

  // TODO(fant): event type?
  onChange = (event: any) => {
    this.props.onChangeText?.(event.target.value);
    if (isNumber(this.textarea.current?.selectionStart)) {
      this.props.onCursorPositionChange?.(this.textarea.current.selectionStart);
    }
  };

  onKeyUp = () => {
    if (!!this.props.onCursorPositionChange) {
      if (isNumber(this.textarea.current?.selectionStart)) {
        this.props.onCursorPositionChange?.(this.textarea.current.selectionStart);
      }
    }
  };

  private isClipboardImage(event: ClipboardEvent): boolean {
    const files = event.clipboardData?.files;
    if (!files || !files.length) {
      return false;
    }
    return Array.from(files).some(file => file.type.startsWith('image'));
  }

  onPaste = (event: ClipboardEvent) => {
    if (!!this.props.onPaste) {
      const pastedText = event.clipboardData?.getData('text');
      if (!pastedText) {
        return;
      }
      const shouldNotPreventDefault = this.props.onPaste(pastedText);
      if (!shouldNotPreventDefault) {
        event.preventDefault();
      }
    }

    if (!!this.props.onPasteFiles && this.isClipboardImage(event)) {
      if (event.clipboardData?.files) {
        this.props.onPasteFiles(Array.from(event.clipboardData.files));
      }
    }
  };

  onHeightChange = () => {
    this.props.onHeightChange?.();
  };

  // TODO(fant): event type?
  handleKeyPress = (event: KeyboardEvent) => {
    // If enter is pressed and shift is not pressed, we want to send the
    // message. Therefore we prevent default (so that the textarea isn't updated)
    if (
      ['Enter', 'NumpadEnter'].includes(event.code) &&
      !event.shiftKey &&
      !!this.props.onSubmitEditing
    ) {
      event.preventDefault();
      this.props.onSubmitEditing?.();
    }
  };

  addKeyPressListener = () =>
    !!this.textarea.current &&
    this.textarea.current.addEventListener('keypress', this.handleKeyPress);
  removeKeyPressListener = () =>
    !!this.textarea.current &&
    this.textarea.current.removeEventListener('keypress', this.handleKeyPress);

  componentDidUpdate() {
    if (this.state.focused) {
      this.addKeyPressListener();
    } else {
      this.removeKeyPressListener();
    }
  }

  componentWillUnmount() {
    this.removeKeyPressListener();
  }

  render() {
    const {
      placeholder,
      value,
      maxRows,
      style,
      autoFocus,
      initialValue,
      bold,
      onKeyPress,
      size = 'normal',
      padding = 'normal',
      paddingVertical,
      color = 'black',
      spellCheck = false,
      align: textAlign = 'left',
      disabled = false,
    } = this.props;

    return (
      <TextArea
        inputRef={this.textarea}
        disabled={disabled}
        readOnly={disabled}
        maxRows={maxRows}
        spellCheck={spellCheck}
        autoFocus={autoFocus}
        placeholder={placeholder}
        value={value}
        onChange={this.onChange}
        onKeyUp={this.onKeyUp}
        onPaste={this.onPaste as any}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        onHeightChange={this.onHeightChange}
        onKeyPress={onKeyPress}
        defaultValue={initialValue}
        style={getStyles({
          paddingVertical,
          size,
          style,
          bold,
          color,
          align: textAlign,
          padding,
        })}
      />
    );
  }
}

function getStyles({ paddingVertical, size, style, bold, color, align, padding }: Props) {
  const styles = {
    ...UnvalidatedStyles.input,
    width: '100%',
    fontWeight: bold ? 'bold' : undefined,
    fontFamily: FONT_STACK,
    ...(() => {
      if (size === 'small') {
        return MBTypographyStylesRaw.body2;
      }
      if (size === 'large') {
        return MBTypographyStylesRaw.h4;
      }
      if (size === 'xlarge') {
        return MBTypographyStylesRaw.h3;
      }
      if (size === 'normal') {
        return MBTypographyStylesRaw.body1;
      }
      return MBTypographyStylesRaw.body1;
    })(),
    color,
    lineHeight: undefined,
    overflow: 'hidden',
    overflowY: 'auto',
    textAlign: align,
    padding: (() => {
      if (padding === 'large') {
        return UnvalidatedStyles.inputPaddingLarge;
      }
      if (padding === 'normal') {
        return UnvalidatedStyles.inputPaddingNormal;
      }
      if (padding === 'none') {
        return 0;
      }
      if (isString(padding)) {
        return padding;
      }
      return 0;
    })(),
    // paddingVertical is ignored if padding is set to a string,
    // which it is by default.
    // can use `padding` prop as "12px 0" to get vertical padding.
    // -nf
    paddingVertical: (() => {
      if (isNumber(paddingVertical)) {
        return paddingVertical;
      }
      if (paddingVertical === 'large') {
        return UnvalidatedStyles.inputPaddingLarge;
      }
      if (paddingVertical === 'normal') {
        return UnvalidatedStyles.inputPaddingNormal;
      }
      return undefined;
    })(),
    ...StyleSheet.flatten(style),
  };

  return styles;
}
